/**
 * BuyerAccountPage — Iter 191u · Storefront "My Account"
 *
 * The buyer-facing hub visible after magic-link sign-in. Three tabs:
 *   • Orders    — GET /public/buyers/{sub}/orders
 *   • Addresses — full CRUD via /public/buyers/{sub}/addresses
 *   • Profile   — name / phone / marketing_consent
 *
 * If the buyer isn't signed in, renders the sign-in / verify UI inline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, MapPin, User, LogOut, Loader2, Plus, Trash2, Star,
  Mail, ArrowLeft, CheckCircle2, XCircle, Truck,
} from 'lucide-react';
import { BuyerAuthProvider, useBuyerAuth } from '@/context/BuyerAuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function centsToDollars(c, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((c || 0) / 100);
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

// ═════════════════════════════════════════════════════════════════
// Sign-in (magic link)
// ═════════════════════════════════════════════════════════════════

function SignInCard() {
  const { subdomain, config, requestMagicLink } = useBuyerAuth();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try {
      await requestMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send sign-in email');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-md mx-auto py-16 px-6" data-testid="buyer-signin-card">
      <div className="rounded-2xl bg-white shadow-xl border border-slate-200 p-8">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Sign in to your account
        </h1>
        <p className="text-sm text-slate-600 mt-2 mb-6">{config.welcome_message}</p>

        {sent ? (
          <div className="text-center py-8">
            <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900">Check your email</h2>
            <p className="text-sm text-slate-600 mt-2">
              We&rsquo;ve sent a sign-in link to <strong className="text-slate-900">{email}</strong>.
              Click the link within 15 minutes to sign in.
            </p>
            <button
              type="button"
              onClick={() => { setSent(false); setEmail(''); }}
              className="mt-6 text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900 underline"
              data-testid="buyer-signin-try-again"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-lg bg-white border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-slate-900 text-sm"
                data-testid="buyer-signin-email"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="buyer-signin-submit"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {sending ? 'Sending…' : 'Send sign-in link'}
            </button>
            <p className="text-[11px] text-slate-500 text-center pt-1">
              No password needed — we&rsquo;ll email you a secure link.
            </p>
          </form>
        )}
      </div>

      <div className="text-center mt-4">
        <Link
          to={`/site/${subdomain}`}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="buyer-signin-back"
        >
          <ArrowLeft size={11} /> Back to store
        </Link>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Orders tab
// ═════════════════════════════════════════════════════════════════

function OrdersTab() {
  const { subdomain, authHeaders } = useBuyerAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/buyers/${subdomain}/orders`, { headers: authHeaders() })
      .then(({ data }) => setOrders(data.orders || []))
      .catch(() => setOrders([]));
  }, [subdomain, authHeaders]);

  if (orders === null) return <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={18} /> Loading…</div>;
  if (orders.length === 0) return (
    <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl bg-slate-50" data-testid="buyer-orders-empty">
      <Package size={36} className="mx-auto text-slate-300 mb-3" />
      <p className="text-sm text-slate-600 font-medium">No orders yet.</p>
      <p className="text-xs text-slate-500 mt-1">Once you place an order, it&rsquo;ll show up here.</p>
      <Link to={`/site/${subdomain}`} className="inline-block mt-5 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-700">
        Browse the store
      </Link>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="buyer-orders-list">
      {orders.map((o) => (
        <div key={o.order_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`buyer-order-${o.order_id}`}>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Order</span>
                <span className="text-xs font-mono text-slate-900">{o.order_id.slice(-10).toUpperCase()}</span>
                <StatusPill status={o.status} />
              </div>
              <div className="text-xs text-slate-500 mt-1">Placed {fmtDate(o.created_at)}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-slate-900">{centsToDollars(o.total_cents, o.currency)}</div>
              <div className="text-[11px] text-slate-500">{(o.items || []).length} item{(o.items || []).length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <div className="space-y-1.5 text-xs text-slate-700 pt-3 border-t border-slate-100">
            {(o.items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between">
                <span>{it.qty || 1} × {it.name || it.title || '—'}</span>
                <span className="text-slate-500">{centsToDollars((it.unit_price || it.price || 0) * (it.qty || 1) * 100)}</span>
              </div>
            ))}
          </div>
          {o.tracking?.code && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs">
              <Truck size={13} className="text-emerald-600" />
              <span className="text-slate-700">
                {o.tracking.carrier?.toUpperCase() || 'Tracking'}: <span className="font-mono">{o.tracking.code}</span>
              </span>
              {o.tracking.label_url && (
                <a href={o.tracking.label_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-violet-600 hover:underline">Label</a>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const palette = {
    paid:      { bg: 'bg-emerald-100', fg: 'text-emerald-700', label: 'Paid' },
    pending:   { bg: 'bg-amber-100',   fg: 'text-amber-700',   label: 'Pending' },
    failed:    { bg: 'bg-red-100',     fg: 'text-red-700',     label: 'Failed' },
    refunded:  { bg: 'bg-slate-200',   fg: 'text-slate-700',   label: 'Refunded' },
    awaiting_payment: { bg: 'bg-sky-100', fg: 'text-sky-700', label: 'Awaiting payment' },
  };
  const p = palette[status] || { bg: 'bg-slate-100', fg: 'text-slate-700', label: status || 'Unknown' };
  return <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider ${p.bg} ${p.fg}`}>{p.label}</span>;
}

// ═════════════════════════════════════════════════════════════════
// Addresses tab
// ═════════════════════════════════════════════════════════════════

const EMPTY_ADDR = {
  label: '', name: '', phone: '', line1: '', line2: '',
  city: '', region: '', postal: '', country: 'US',
  is_default_shipping: false, is_default_billing: false,
};

function AddressesTab() {
  const { subdomain, authHeaders } = useBuyerAuth();
  const [addresses, setAddresses] = useState(null);
  const [editing, setEditing] = useState(null); // address_id | 'new'
  const [draft, setDraft]     = useState(EMPTY_ADDR);
  const [saving, setSaving]   = useState(false);

  const load = React.useCallback(() => {
    axios.get(`${API}/public/buyers/${subdomain}/addresses`, { headers: authHeaders() })
      .then(({ data }) => setAddresses(data.addresses || []))
      .catch(() => setAddresses([]));
  }, [subdomain, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setDraft(EMPTY_ADDR); setEditing('new'); };
  const startEdit = (a) => { setDraft({ ...a }); setEditing(a.address_id); };
  const cancel    = () => { setEditing(null); setDraft(EMPTY_ADDR); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/public/buyers/${subdomain}/addresses`, draft, { headers: authHeaders() });
        toast.success('Address added');
      } else {
        await axios.put(`${API}/public/buyers/${subdomain}/addresses/${editing}`, draft, { headers: authHeaders() });
        toast.success('Address updated');
      }
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save address');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await axios.delete(`${API}/public/buyers/${subdomain}/addresses/${id}`, { headers: authHeaders() });
      toast.success('Address removed');
      load();
    } catch { toast.error('Could not remove'); }
  };

  if (addresses === null) return <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={18} /> Loading…</div>;

  return (
    <div data-testid="buyer-addresses-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Saved addresses</h2>
        {!editing && (
          <button
            onClick={startNew}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-900 hover:bg-slate-700 text-white"
            data-testid="buyer-address-new"
          >
            <Plus size={12} /> Add address
          </button>
        )}
      </div>

      {editing && <AddressForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />}

      {!editing && addresses.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl bg-slate-50" data-testid="buyer-addresses-empty">
          <MapPin size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-600 font-medium">No saved addresses yet.</p>
          <p className="text-xs text-slate-500 mt-1">Add one to speed up future checkouts.</p>
        </div>
      )}

      {!editing && addresses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {addresses.map((a) => (
            <div key={a.address_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`buyer-address-${a.address_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    {a.label || 'Address'}
                    {a.is_default_shipping && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase tracking-wider">Default ship</span>}
                    {a.is_default_billing && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 uppercase tracking-wider">Default bill</span>}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-2 leading-relaxed">
                    {a.name && <div className="font-semibold text-slate-800">{a.name}</div>}
                    <div>{a.line1}{a.line2 ? `, ${a.line2}` : ''}</div>
                    <div>{a.city}{a.region ? `, ${a.region}` : ''} {a.postal}</div>
                    <div>{a.country}</div>
                    {a.phone && <div className="mt-1 text-slate-500">{a.phone}</div>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
                <button onClick={() => startEdit(a)} className="text-[11px] uppercase tracking-wider text-slate-700 hover:text-slate-900 font-bold" data-testid={`buyer-address-edit-${a.address_id}`}>Edit</button>
                <button onClick={() => remove(a.address_id)} className="text-[11px] uppercase tracking-wider text-red-600 hover:text-red-800 font-bold ml-auto flex items-center gap-1" data-testid={`buyer-address-delete-${a.address_id}`}>
                  <Trash2 size={11} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddrInput(props) {
  return (
    <input {...props} className={`w-full px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-slate-900 text-sm ${props.className || ''}`} />
  );
}

function AddressForm({ draft, setDraft, onSave, onCancel, saving }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4" data-testid="buyer-address-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Label (e.g. Home, Office)</Label>
          <AddrInput value={draft.label || ''} onChange={(e) => set('label', e.target.value)} placeholder="Home" data-testid="buyer-address-label" />
        </div>
        <div>
          <Label>Full name</Label>
          <AddrInput value={draft.name || ''} onChange={(e) => set('name', e.target.value)} data-testid="buyer-address-name" />
        </div>
        <div>
          <Label>Phone (optional)</Label>
          <AddrInput value={draft.phone || ''} onChange={(e) => set('phone', e.target.value)} data-testid="buyer-address-phone" />
        </div>
        <div className="md:col-span-2">
          <Label>Address</Label>
          <AddrInput required value={draft.line1 || ''} onChange={(e) => set('line1', e.target.value)} placeholder="123 Cedar St" data-testid="buyer-address-line1" />
        </div>
        <div className="md:col-span-2">
          <Label>Apt / Suite (optional)</Label>
          <AddrInput value={draft.line2 || ''} onChange={(e) => set('line2', e.target.value)} data-testid="buyer-address-line2" />
        </div>
        <div>
          <Label>City</Label>
          <AddrInput required value={draft.city || ''} onChange={(e) => set('city', e.target.value)} data-testid="buyer-address-city" />
        </div>
        <div>
          <Label>State / Region</Label>
          <AddrInput value={draft.region || ''} onChange={(e) => set('region', e.target.value)} data-testid="buyer-address-region" />
        </div>
        <div>
          <Label>Postal / ZIP</Label>
          <AddrInput value={draft.postal || ''} onChange={(e) => set('postal', e.target.value)} data-testid="buyer-address-postal" />
        </div>
        <div>
          <Label>Country</Label>
          <AddrInput value={draft.country || ''} onChange={(e) => set('country', e.target.value)} data-testid="buyer-address-country" />
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!draft.is_default_shipping} onChange={(e) => set('is_default_shipping', e.target.checked)} data-testid="buyer-address-default-ship" /> Default shipping
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!draft.is_default_billing} onChange={(e) => set('is_default_billing', e.target.checked)} data-testid="buyer-address-default-bill" /> Default billing
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-100" data-testid="buyer-address-cancel">Cancel</button>
        <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-bold bg-slate-900 hover:bg-slate-700 text-white disabled:opacity-50 flex items-center gap-1.5" data-testid="buyer-address-save">
          {saving && <Loader2 size={12} className="animate-spin" />} Save address
        </button>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</label>;
}

// ═════════════════════════════════════════════════════════════════
// Profile tab
// ═════════════════════════════════════════════════════════════════

function ProfileTab() {
  const { buyer, updateProfile } = useBuyerAuth();
  const [name, setName]           = useState(buyer?.name || '');
  const [phone, setPhone]         = useState(buyer?.phone || '');
  const [marketing, setMarketing] = useState(!!buyer?.marketing_consent);
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ name, phone, marketing_consent: marketing });
      toast.success('Profile saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-lg" data-testid="buyer-profile-tab">
      <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Your profile</h2>
      <div className="space-y-4">
        <div>
          <Label>Email</Label>
          <input value={buyer?.email || ''} disabled className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 text-sm" data-testid="buyer-profile-email" />
          <p className="text-[10.5px] text-slate-500 mt-1">To change your email, sign in with the new address.</p>
        </div>
        <div>
          <Label>Full name</Label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-sm" data-testid="buyer-profile-name" />
        </div>
        <div>
          <Label>Phone</Label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-sm" data-testid="buyer-profile-phone" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-1">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="buyer-profile-marketing" />
          Send me occasional news and offers
        </label>
      </div>
      <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
        <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-1.5" data-testid="buyer-profile-save">
          {saving && <Loader2 size={12} className="animate-spin" />} Save profile
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Signed-in shell — tabs + sign-out
// ═════════════════════════════════════════════════════════════════

function AccountShell() {
  const { buyer, signOut, subdomain } = useBuyerAuth();
  const [tab, setTab] = useState('orders');

  const tabs = [
    { id: 'orders',    label: 'Orders',    Icon: Package },
    { id: 'addresses', label: 'Addresses', Icon: MapPin },
    { id: 'profile',   label: 'Profile',   Icon: User },
  ];

  return (
    <div className="max-w-4xl mx-auto py-10 px-4" data-testid="buyer-account-shell">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">My Account</p>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
            Welcome, {buyer?.name || buyer?.email?.split('@')[0] || 'friend'}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Signed in as {buyer?.email}</p>
        </div>
        <button
          onClick={signOut}
          className="text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1.5"
          data-testid="buyer-signout"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>

      <div className="border-b border-slate-200 mb-8 flex gap-2 overflow-x-auto">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
            data-testid={`buyer-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
        <Link
          to={`/site/${subdomain}`}
          className="ml-auto px-4 py-3 text-xs uppercase tracking-wider font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
          data-testid="buyer-back-to-store"
        >
          <ArrowLeft size={12} /> Back to store
        </Link>
      </div>

      {tab === 'orders'    && <OrdersTab />}
      {tab === 'addresses' && <AddressesTab />}
      {tab === 'profile'   && <ProfileTab />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Public exports
// ═════════════════════════════════════════════════════════════════

function AccountRouterInner() {
  const { signedIn, ready, config } = useBuyerAuth();
  if (!ready) return <div className="py-16 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></div>;
  if (!config.enabled) {
    return (
      <div className="max-w-md mx-auto py-16 px-6 text-center" data-testid="buyer-accounts-disabled">
        <XCircle size={36} className="mx-auto text-slate-400 mb-3" />
        <h1 className="text-lg font-bold text-slate-900">Accounts are turned off</h1>
        <p className="text-sm text-slate-600 mt-2">This store isn&rsquo;t accepting customer sign-ups at the moment.</p>
      </div>
    );
  }
  return signedIn ? <AccountShell /> : <SignInCard />;
}

function VerifyPageInner() {
  const [params]  = useSearchParams();
  const token     = params.get('token');
  const { verifyToken, subdomain } = useBuyerAuth();
  const nav = useNavigate();
  const [state, setState] = useState('pending'); // pending | success | error
  const [msg, setMsg]     = useState('Signing you in…');

  useEffect(() => {
    if (!token) { setState('error'); setMsg('No sign-in token found.'); return; }
    (async () => {
      try {
        await verifyToken(token);
        setState('success');
        setMsg('Signed in!');
        setTimeout(() => nav(`/site/${subdomain}/account`, { replace: true }), 900);
      } catch (e) {
        setState('error');
        setMsg(e?.response?.data?.detail || 'This link is invalid or expired.');
      }
    })();
  }, [token, verifyToken, nav, subdomain]);

  return (
    <div className="max-w-md mx-auto py-24 px-6 text-center" data-testid="buyer-verify-page">
      {state === 'pending' && <><Loader2 size={36} className="animate-spin text-slate-500 mx-auto mb-3" /><p className="text-sm text-slate-600">{msg}</p></>}
      {state === 'success' && <><CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" /><h1 className="text-lg font-bold text-slate-900">{msg}</h1></>}
      {state === 'error'   && (
        <>
          <XCircle size={40} className="text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900">Couldn&rsquo;t sign you in</h1>
          <p className="text-sm text-slate-600 mt-2">{msg}</p>
          <Link
            to={`/site/${subdomain}/account/sign-in`}
            className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-slate-900 text-white text-xs uppercase tracking-wider font-bold hover:bg-slate-700"
            data-testid="buyer-verify-retry"
          >
            Request a new link
          </Link>
        </>
      )}
    </div>
  );
}

export default function BuyerAccountPage() {
  const { subdomain } = useParams();
  return (
    <BuyerAuthProvider subdomain={subdomain}>
      <div className="min-h-screen bg-slate-50">
        <AccountRouterInner />
      </div>
    </BuyerAuthProvider>
  );
}

export function BuyerVerifyPage() {
  const { subdomain } = useParams();
  return (
    <BuyerAuthProvider subdomain={subdomain}>
      <div className="min-h-screen bg-slate-50">
        <VerifyPageInner />
      </div>
    </BuyerAuthProvider>
  );
}
